/**
 * Плоские строки таблиц — зеркалят будущую схему БД.
 * Каждый тип = одна таблица на бэке. FK через *Id поля.
 *
 * Иерархия (после миграции май-2026):
 * Sphere → Theme (бывш. Category) → Subtheme (бывш. Theme) → SemanticUnit → Angle → Ad
 */

import type { NodeStatus, NodeHistoryEntry, AdGroupType } from '../types';

export interface DbProduct {
  id: string;
  schoolId?: string;
  name: string;
  description?: string;
  status: string;
  authorId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface DbSphere {
  id: string;
  productId: string;
  name: string;
  description?: string;
  displayOrder: number;
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
  createdAt: string;
}

/** Тема (бывш. Category). */
export interface DbTheme {
  id: string;
  sphereId: string;
  name: string;
  description?: string;
  keywords?: string[];
  images?: string[];
  displayOrder: number;
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
  createdAt: string;
}

/** Подтема (бывш. Theme). */
export interface DbSubtheme {
  id: string;
  themeId: string;
  name: string;
  description?: string;
  keywords?: string[];
  images?: string[];
  displayOrder: number;
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
  createdAt: string;
}

export interface DbSemanticUnit {
  id: string;
  subthemeId: string;
  name: string;
  description?: string;
  examples?: string[];
  keywords?: string[];
  displayOrder: number;
  status?: NodeStatus;
  priority?: number;
  history?: NodeHistoryEntry[];
  createdAt: string;
}

export interface DbOffer {
  id: string;
  productId: string;
  name: string;
  description?: string;
  keyPromise?: string;
  averageCheck?: number | null;
  metrikaConversionId?: string;
  desiredConversionCost?: number | null;
  createdAt: string;
  updatedAt?: string;
}

export type OfferBindingScope = 'product' | 'sphere' | 'theme' | 'subtheme';

export interface DbOfferBinding {
  id: string;
  offerId: string;
  scope: OfferBindingScope;
  scopeId: string;
  createdAt: string;
}

export interface DbOfferAddress {
  id: string;
  offerId: string;
  name: string;
  url: string;
  pageGoal: string;
  pageText?: string;
}

export interface DbOfferRkBinding {
  id: string;
  offerId: string;
  cabinetId: string;
  rkName: string;
  rules: { id: string; type: 'all' | 'contains' | 'campaign_id' | 'exact_name'; value: string }[];
}

export interface DbCampaign {
  id: string;
  productId: string;
  name: string;
  platform?: string;
  status?: string;
  createdAt: string;
}

export interface DbSchoolImage {
  id: string;
  schoolId: string;
  url: string;
  name: string;
  tags?: string[];
  createdAt: string;
}

/**
 * Для следующих таблиц оставляем доменный тип как есть (он уже плоский).
 * Когда появится бэк — переведём на отдельные Db* интерфейсы с snake_case.
 */
import type { Product, Angle, Ad, AdCabinet, Campaign, AdGroup, CampaignAd, AutoModeSettings } from '../types';

export interface DbDatabase {
  products: Product[];
  spheres: DbSphere[];
  /** Темы (бывш. categories). */
  themes: DbTheme[];
  /** Подтемы (бывш. themes). */
  subthemes: DbSubtheme[];
  semanticUnits: DbSemanticUnit[];
  offers: DbOffer[];
  offerBindings: DbOfferBinding[];
  offerAddresses: DbOfferAddress[];
  offerRkBindings: DbOfferRkBinding[];
  campaigns: Campaign[];
  adGroups: AdGroup[];
  campaignAds: CampaignAd[];
  angles: Angle[];
  ads: Ad[];
  adCabinets: AdCabinet[];
  productCabinetLinks: { productId: string; cabinetId: string }[];
  schoolImages: DbSchoolImage[];
  autoModeSettings: AutoModeSettings[];
}

export const emptyDb = (): DbDatabase => ({
  products: [],
  spheres: [],
  themes: [],
  subthemes: [],
  semanticUnits: [],
  offers: [],
  offerBindings: [],
  offerAddresses: [],
  offerRkBindings: [],
  campaigns: [],
  adGroups: [],
  campaignAds: [],
  angles: [],
  ads: [],
  adCabinets: [],
  productCabinetLinks: [],
  schoolImages: [],
  autoModeSettings: [],
});

/**
 * Миграция legacy-структур (categories/themes старого формата) к новой иерархии.
 * Применяется при загрузке БД из localStorage.
 */
export const migrateDbShape = (raw: any): DbDatabase => {
  if (!raw || typeof raw !== 'object') return emptyDb();
  const base = emptyDb();
  // categories → themes (если темы новые ещё пустые)
  if (raw.categories && Array.isArray(raw.categories) && (!raw.themes || raw.themes.length === 0 || raw.themes[0]?.categoryId !== undefined)) {
    raw.themes = raw.categories;
    delete raw.categories;
  }
  // themes старого формата → subthemes
  if (raw.themes && Array.isArray(raw.themes) && raw.themes[0]?.categoryId !== undefined) {
    raw.subthemes = raw.themes.map((t: any) => ({ ...t, themeId: t.categoryId }));
    raw.themes = raw.categories || [];
    delete raw.categories;
  }
  // SemanticUnit.themeId → subthemeId
  if (raw.semanticUnits && Array.isArray(raw.semanticUnits)) {
    raw.semanticUnits = raw.semanticUnits.map((u: any) =>
      u.themeId && !u.subthemeId ? { ...u, subthemeId: u.themeId } : u,
    );
  }
  return { ...base, ...raw } as DbDatabase;
};
